import { z } from 'zod';
import { UpdatePartnerPayload } from '../../../api/queries';

export const BASIS_POINTS_PER_PERCENT = 100;

export const editPartnerSchema = z.object({
  contactEmail: z.string().email('Ugyldig e-postadresse').optional().or(z.literal('')),
  contactPhone: z
    .string()
    .optional()
    .refine(
      (value) => !value || /^[\d\s+()-]*\d[\d\s+()-]*$/.test(value),
      'Telefonnummer må inneholde minst ett siffer'
    ),
  cashbackPercentage: z
    .string()
    .optional()
    .refine(
      (value) => !value || /^\d+(\.\d{0,2})?$/.test(value),
      'Cashback-prosent må være et gyldig tall med maks to desimaler'
    )
    .refine(
      (value) => {
        if (!value) return true;
        const num = parseFloat(value);
        return num >= 0 && num <= 100;
      },
      'Cashback-prosent må være mellom 0 og 100'
    ),
});

export type EditPartnerFormData = z.infer<typeof editPartnerSchema>;

export const convertPercentageToBasisPoints = (percentage: string): number | undefined => {
  const value = parseFloat(percentage);
  return isNaN(value) ? undefined : Math.round(value * BASIS_POINTS_PER_PERCENT);
};

export const convertBasisPointsToPercentage = (basisPoints: number | null): string => {
  return basisPoints !== null ? (basisPoints / BASIS_POINTS_PER_PERCENT).toFixed(2) : '';
};

export const buildUpdatePayload = (formData: EditPartnerFormData): UpdatePartnerPayload => {
  const payload: UpdatePartnerPayload = {};

  if (formData.contactEmail?.trim()) {
    payload.contactEmail = formData.contactEmail.trim();
  }

  if (formData.contactPhone?.trim()) {
    payload.contactPhone = formData.contactPhone.trim();
  }

  if (formData.cashbackPercentage?.trim()) {
    const basisPoints = convertPercentageToBasisPoints(formData.cashbackPercentage);
    if (basisPoints !== undefined) {
      payload.cashbackPercentage = basisPoints;
    }
  }

  return payload;
};

export const extractValidationErrors = (zodError: z.ZodError): Partial<Record<keyof EditPartnerFormData, string>> => {
  return zodError.errors.reduce((acc, err) => {
    if (err.path[0]) {
      acc[err.path[0] as keyof EditPartnerFormData] = err.message;
    }
    return acc;
  }, {} as Partial<Record<keyof EditPartnerFormData, string>>);
};

export const isValidCashbackInput = (value: string): boolean => !value || /^\d+(\.\d{0,2})?$/.test(value);
